import { MouseEventHandler, useCallback, useMemo } from "react"
import {
  Box,
  CardMedia,
  Fade,
  FadeProps,
  IconButton,
  Modal,
  ModalProps,
  SxProps,
  useTheme,
} from "@mui/material"
import { deepmerge } from "@mui/utils"
import _ from "lodash"
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded"
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded"
import clsx from "clsx"
import { getIcon } from "utils/icon"

import { CloseIconButton } from "components/Button"
import { Carousel, CarouselItem } from "components/Carousel"
import { IExtendOptions } from "components/Carousel/Carousel"
import { CloseIcon } from "components/icons"
import { SplideTrack } from "@splidejs/react-splide"
export interface ImageCarouselModalProps
  extends Pick<ModalProps, "onClose" | "className"> {
  open: boolean
  activeSrc: string
  imageSrcs: string[]
  fadeProps?: FadeProps
  carouselOptions?: IExtendOptions
  sx?: SxProps
}

export const ImageCarouselModal: React.FC<ImageCarouselModalProps> = ({
  open = false,
  activeSrc,
  fadeProps = {},
  imageSrcs = [],
  onClose,
  carouselOptions: {
    type: defaultCarouselType = "slide",
    ...carouselOptions
  } = {},
  sx = {},
  ...props
}) => {
  const theme = useTheme()
  const styleOverrides =
    theme.components[`AppImageCarouselModal`]?.styleOverrides || {}

  const {
    root,
    carouselContainer,
    closeButton,
    carousel,
    carouselItem,
    carouselArrows,
    image,
    prevButton,
    nextButton,
    backdrop,
  } = styleOverrides

  const closeIcon = useMemo(
    () =>
      getIcon(theme?.assets?.imageCarouselModal?.close) || (
        <CloseIcon fontSize="inherit" />
      ),
    [theme]
  )
  const prevIcon = useMemo(
    () =>
      getIcon(theme?.assets?.imageCarouselModal?.prev) || (
        <ArrowBackIosNewRoundedIcon fontSize="inherit" />
      ),
    [theme]
  )
  const nextIcon = useMemo(
    () =>
      getIcon(theme?.assets?.imageCarouselModal?.next) || (
        <ArrowForwardIosRoundedIcon fontSize="inherit" />
      ),
    [theme]
  )

  const onReady = useCallback(
    (splide) => {
      if (_.size(imageSrcs) <= 0 || !activeSrc) return
      const activeSlideIndex = imageSrcs.findIndex((src) => activeSrc === src)
      splide.go(activeSlideIndex)
    },
    [imageSrcs, activeSrc]
  )

  const carouselType = useMemo(() => {
    return imageSrcs.length > 1 ? defaultCarouselType : "slide"
  }, [imageSrcs.length, defaultCarouselType])

  const isRtl = theme.isRtl
  const direction = theme.direction

  return (
    <Modal
      sx={deepmerge(root, sx)}
      open={open}
      onClose={onClose}
      slotProps={{
        backdrop: {
          //@ts-ignore
          sx: backdrop,
        },
      }}
      {...props}
    >
      <Fade in={open} timeout={500} {...fadeProps}>
        <Box sx={carouselContainer}>
          <CloseIconButton
            className="AppImageCarouselModal-closeButton"
            sx={closeButton}
            icon={closeIcon}
            size="small"
            onClick={onClose as MouseEventHandler<HTMLButtonElement>}
          />
          <Carousel
            onReady={onReady}
            hasTrack={false}
            options={{
              trimSpace: false,
              pagination: true,
              perPage: 1,
              focus: "center",
              gap: 0,
              arrows: true,
              limitPaginations: 9,
              type: carouselType,
              direction: direction,
              ...carouselOptions,
            }}
            className="AppImageCarouselModal-carousel"
            sx={carousel}
          >
            <SplideTrack>
              {imageSrcs.map((src, index) => {
                return (
                  <CarouselItem
                    className="AppImageCarouselModal-carouselItem"
                    key={`slide-${index}`}
                    sx={carouselItem}
                  >
                    <CardMedia
                      className="AppImageCarouselModal-image"
                      sx={image}
                      image={src}
                    />
                  </CarouselItem>
                )
              })}
            </SplideTrack>
            <Box
              sx={{
                ...carouselArrows,
                visibility: _.size(imageSrcs) > 1 ? "visibility" : "hidden",
              }}
              className="splide__arrows"
            >
              <IconButton
                sx={prevButton}
                aria-label="Prev slide"
                color="inherit"
                size="small"
                className={clsx("splide__arrow--prev", { "is-rtl": isRtl })}
              >
                {prevIcon}
              </IconButton>
              <IconButton
                sx={nextButton}
                aria-label="Next slide"
                color="inherit"
                size="small"
                className="splide__arrow--next"
              >
                {nextIcon}
              </IconButton>
            </Box>
          </Carousel>
        </Box>
      </Fade>
    </Modal>
  )
}
